import styled from "styled-components";
import { type SpaceProps, space } from "styled-system";
import { theme } from "../../../../../../../common-deprecated/styles/v2/lexus/theme/lexusTheme";
import * as Button from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import { getBreakpoint } from "../../../../../../../common-deprecated/themes/common";
import { ButtonLink } from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import { AvailabilityBadges } from "../../../../../../../common-deprecated/components/aem/lexus/availability-badges/styles/AvailabilityBadgesStyles";

export const Grid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-auto-rows: min-content;

    @media ${getBreakpoint("up", "md")} {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-column: 1 / span 4;
    }

    @media ${getBreakpoint("up", "lg")} {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        grid-column: 2 / span 5;
    }
`;

export const Container = styled.div<SpaceProps>`
    ${space}

    @media ${getBreakpoint("down", "md")} {
        padding-top: ${theme.space[3]}px;
        border-top: 1px solid ${theme.colors.silver};
    }
`;

export const LegalBannerContainer = styled(Container)`
    max-width: 1440px;
    margin: 0 auto;
    padding: 40px 16px;
    @media ${getBreakpoint("up", "lg")} {
        padding: 80px 40px;
    }
`;

export const Header = styled.div`
    padding-bottom: ${theme.space[5]}px;
    text-align: center;
    @media ${getBreakpoint("up", "md")} {
        border-bottom: 1px solid ${theme.colors.silver};
    }
`;

export const HeaderWrapper = styled.div`
    margin-bottom: ${theme.space[1]}px;
`;

export const Main = styled.div`
    display: flex;
    flex-wrap: wrap;
    @media ${getBreakpoint("up", "lg")} {
        flex-wrap: nowrap;
    }
`;

export const MainGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    justify-content: center;

    @media ${getBreakpoint("up", "md")} {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media ${getBreakpoint("up", "lg")} {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
`;

export const Wrapper = styled.div<{ hasRightBorder?: boolean }>`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-items: flex-start;
    justify-content: center;
    height: 100%;
    padding: ${theme.space[5]}px ${theme.space[3]}px;

    @media ${getBreakpoint("up", "md")} {
        border-bottom: 1px solid ${theme.colors.silver};
        border-right: 1px solid ${theme.colors.silver};
        &:nth-child(2n) {
            border-right: 1px solid ${theme.colors.silver};
        }
    }

    @media ${getBreakpoint("up", "xl")} {
        &:nth-child(1n) {
            border-right: 1px solid ${theme.colors.silver};
        }
        padding: ${theme.space[6]}px 20px;
    }

    ${AvailabilityBadges} {
        margin-bottom: ${theme.space[2]}px;
    }

    html[dir="rtl"] & {
        border-left: 1px solid ${theme.colors.silver};
        border-right: none;
        @media ${getBreakpoint("up", "md")} {
            &:nth-child(2n) {
                border-left: none;
            }
        }
        @media ${getBreakpoint("up", "lg")} {
            border-left: 1px solid ${theme.colors.silver};
            border-right: none;
            &:nth-child(1n) {
                border-left: 1px solid ${theme.colors.silver};
                border-right: none;
            }
            &:nth-child(3n) {
                border-left: none;
            }
        }
    }
`;

export const ImageWrapper = styled.a`
    width: 100%;
    margin-bottom: ${theme.space[5]}px;
`;

export const Heading = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    justify-content: flex-start;
    padding-inline: 2rem;
    margin-block-end: 2rem;

    @supports not (padding-inline: 0) {
        padding-left: 2rem;
        padding-right: 2rem;
    }
`;

export const Title = styled.div.attrs(() => ({
    className: "l-beta-text",
}))`
    margin-bottom: 0;
    font-family: ${theme.fonts.regular};
    font-size: 1.8rem;
    letter-spacing: 1.3%;
    line-height: 2.4rem;
    text-transform: none;
    color: ${theme.colors.midnight};

    @media ${getBreakpoint("up", "lg")} {
        font-size: 4.4rem;
        line-height: 4.6rem;
    }
`;

export const Subtitle = styled.div.attrs(() => ({
    className: "l-micro-text",
}))`
    align-self: center;
    margin-left: ${theme.space[3]}px;
    font-size: 1.8rem;
    line-height: 2.4rem;
    text-transform: uppercase;
    color: ${theme.colors.midnight};

    @media ${getBreakpoint("up", "lg")} {
        font-size: 1.4rem;
        line-height: 2.4rem;
    }

    html[dir="rtl"] & {
        margin-left: 0;
        margin-right: ${theme.space[3]}px;
    }
`;

export const EcoBadgeWrapper = styled.div`
    display: flex;
    position: absolute;
    bottom: -4.5rem;
    right: 0;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    margin-block-start: 1rem;
`;

export const GradeLink = styled(Button.Link)`
    font-family: ${theme.fonts.bold};
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${theme.colors.azure};
    &:hover {
        color: ${theme.colors.azureHover};
    }
`;

export const Actions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin-top: auto;
    padding-inline: 2rem;

    ${ButtonLink} {
        margin-right: 0 !important;
        .l-icon::before {
            margin-top: -2px;
        }
    }

    @supports not (padding-inline: 0) {
        padding-left: 2rem;
        padding-right: 2rem;
    }
`;

export const Eta = styled.div`
    display: flex;
    align-items: center;
    gap: calc(${theme.space[1]}px / 2);
    margin: 0;
    font-family: ${theme.fonts.book};
    font-size: 1.4rem;
    color: ${theme.colors.steel};

    p:first-child {
        margin: 0;
    }
`;

export const DisclaimerIcon = styled.span`
    display: flex;
    align-items: center;
    color: ${theme.colors.steel};
    cursor: pointer;
`;
